
import React from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from "@/components/ui/card";
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, ResponsiveContainer, Legend, Tooltip } from "recharts";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Award, BookOpen, CreditCard, TrendingUp, Users, Star, ChevronRight, GraduationCap, DollarSign } from "lucide-react";

export const PremiumSubscriptions = () => {
  // Force render to ensure the charts display properly
  const [forceUpdate, setForceUpdate] = React.useState(0);

  React.useEffect(() => {
    // Small delay to ensure component is mounted before trying to render charts
    const timer = setTimeout(() => {
      setForceUpdate(prev => prev + 1);
    }, 100);
    
    return () => clearTimeout(timer);
  }, []);

  // Mock data - in a real app, this would come from an API
  const subscriberGrowthData = [
    { month: "Jan", subscriberCount: 1820, conversionRate: 12 },
    { month: "Feb", subscriberCount: 2050, conversionRate: 14 },
    { month: "Mar", subscriberCount: 2280, conversionRate: 15 },
    { month: "Apr", subscriberCount: 2570, conversionRate: 16 },
    { month: "May", subscriberCount: 2780, conversionRate: 18 },
    { month: "Jun", subscriberCount: 2950, conversionRate: 19 },
    { month: "Jul", subscriberCount: 3140, conversionRate: 21 },
    { month: "Aug", subscriberCount: 3315, conversionRate: 22 },
  ];

  const badgeCompletionData = [
    { name: "Communication Skills", completions: 2463, avgScore: 87 },
    { name: "Empathy", completions: 2184, avgScore: 92 },
    { name: "Crisis Management", completions: 1957, avgScore: 79 },
    { name: "Conflict Resolution", completions: 1843, avgScore: 81 },
    { name: "Time Management", completions: 1768, avgScore: 85 },
    { name: "Leadership", completions: 1645, avgScore: 78 },
    { name: "Stress Management", completions: 1593, avgScore: 83 },
  ];

  const revenueData = [
    { month: "Jan", revenue: 18018, growthRate: 0 },
    { month: "Feb", revenue: 20295, growthRate: 12.6 },
    { month: "Mar", revenue: 22572, growthRate: 11.2 },
    { month: "Apr", revenue: 25443, growthRate: 12.7 },
    { month: "May", revenue: 27522, growthRate: 8.2 },
    { month: "Jun", revenue: 29205, growthRate: 6.1 },
    { month: "Jul", revenue: 31086, growthRate: 6.4 },
    { month: "Aug", revenue: 32818.5, growthRate: 5.6 },
  ];

  const resourceAccessData = [
    { name: "Video Courses", accessCount: 18743, completionRate: 68 },
    { name: "Interactive Workshops", accessCount: 12845, completionRate: 71 },
    { name: "Case Studies", accessCount: 9876, completionRate: 82 },
    { name: "Skill Assessments", accessCount: 15432, completionRate: 93 },
    { name: "Expert Webinars", accessCount: 7654, completionRate: 58 },
    { name: "Community Forums", accessCount: 22145, completionRate: 76 },
  ];

  return (
    <div className="space-y-4" key={forceUpdate}>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card className="bg-gradient-to-br from-blue-50 to-blue-100">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-blue-700">Total Subscribers</p>
                <h3 className="mt-1 text-3xl font-bold">3,315</h3>
                <p className="mt-1 text-sm text-green-600">+5.6% from last month</p>
              </div>
              <div className="rounded-full bg-blue-200 p-2">
                <CreditCard className="h-6 w-6 text-blue-700" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-green-50 to-green-100">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-green-700">Monthly Revenue</p>
                <h3 className="mt-1 text-3xl font-bold">€32,818</h3>
                <p className="mt-1 text-sm text-green-600">€9.90 per subscriber</p>
              </div>
              <div className="rounded-full bg-green-200 p-2">
                <DollarSign className="h-6 w-6 text-green-700" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-purple-50 to-purple-100">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-purple-700">Resource Engagement</p>
                <h3 className="mt-1 text-3xl font-bold">87%</h3>
                <p className="mt-1 text-sm text-green-600">+3.2% from last month</p>
              </div>
              <div className="rounded-full bg-purple-200 p-2">
                <BookOpen className="h-6 w-6 text-purple-700" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-amber-50 to-amber-100">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-amber-700">Badges Earned</p>
                <h3 className="mt-1 text-3xl font-bold">12,453</h3>
                <p className="mt-1 text-sm text-green-600">+8.7% from last month</p>
              </div>
              <div className="rounded-full bg-amber-200 p-2">
                <Award className="h-6 w-6 text-amber-700" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-blue-500" />
              Subscriber Growth
            </CardTitle>
            <CardDescription>Monthly growth of premium subscribers</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={subscriberGrowthData}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="month" />
                  <YAxis 
                    yAxisId="left" 
                    orientation="left"
                    tickFormatter={(value) => `${value}`} 
                  />
                  <YAxis 
                    yAxisId="right" 
                    orientation="right" 
                    tickFormatter={(value) => `${value}%`}
                  />
                  <Tooltip 
                    formatter={(value, name) => {
                      if (name === "subscriberCount") return [value, "Subscribers"];
                      if (name === "conversionRate") return [`${value}%`, "Conversion Rate"];
                      return [value, name];
                    }}
                    contentStyle={{ background: "#fff", border: "1px solid #ccc", borderRadius: "4px", padding: "10px" }}
                  />
                  <Line 
                    yAxisId="left"
                    type="monotone" 
                    dataKey="subscriberCount" 
                    stroke="#3B82F6" 
                    name="Subscribers"
                    strokeWidth={2}
                    dot={{ r: 3 }}
                    activeDot={{ r: 5 }}
                  />
                  <Line 
                    yAxisId="right"
                    type="monotone" 
                    dataKey="conversionRate" 
                    stroke="#10B981" 
                    name="Conversion Rate"
                    strokeWidth={2}
                    dot={{ r: 3 }}
                    activeDot={{ r: 5 }}
                  />
                  <Legend />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Award className="h-5 w-5 text-amber-500" />
              Top Soft Skills Badges
            </CardTitle>
            <CardDescription>Most completed badges by professionals</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart 
                  data={badgeCompletionData} 
                  layout="vertical"
                  margin={{ left: 120 }}
                >
                  <CartesianGrid strokeDasharray="3 3" horizontal={true} vertical={false} />
                  <XAxis type="number" />
                  <YAxis 
                    dataKey="name" 
                    type="category" 
                    axisLine={false} 
                    tickLine={false}
                  />
                  <Tooltip 
                    formatter={(value, name) => {
                      if (name === "completions") return [value, "Completions"];
                      if (name === "avgScore") return [`${value}%`, "Avg Score"];
                      return [value, name];
                    }}
                    contentStyle={{ background: "#fff", border: "1px solid #ccc", borderRadius: "4px", padding: "10px" }}
                  />
                  <Bar 
                    dataKey="completions" 
                    fill="#F59E0B" 
                    name="Completions"
                    radius={[0, 4, 4, 0]}
                  />
                  <Legend />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BookOpen className="h-5 w-5 text-purple-500" />
            Resource Center Access
          </CardTitle>
          <CardDescription>Resource usage and completion rates</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
            {resourceAccessData.map((resource, index) => (
              <Card key={index} className="bg-gray-50">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <h4 className="font-semibold">{resource.name}</h4>
                    <Badge 
                      className={`${
                        resource.completionRate >= 80 
                          ? "bg-green-100 text-green-700" 
                          : resource.completionRate >= 65 
                          ? "bg-amber-100 text-amber-700" 
                          : "bg-red-100 text-red-700"
                      }`}
                    >
                      {resource.completionRate}% completion
                    </Badge>
                  </div>
                  <div className="mt-2 text-sm text-muted-foreground">
                    {resource.accessCount.toLocaleString()} total accesses
                  </div>
                  <div className="mt-4 h-2 w-full rounded-full bg-gray-200">
                    <div 
                      className={`h-2 rounded-full ${
                        resource.completionRate >= 80 
                          ? "bg-green-500" 
                          : resource.completionRate >= 65 
                          ? "bg-amber-500" 
                          : "bg-red-500"
                      }`}
                      style={{ width: `${resource.completionRate}%` }}
                    ></div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </CardContent>
        <CardFooter className="flex justify-center pt-2">
          <Button variant="outline" className="gap-1">
            View Detailed Resource Analytics <ChevronRight className="h-4 w-4" />
          </Button>
        </CardFooter>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <GraduationCap className="h-5 w-5 text-blue-500" />
            Soft Skills Learning Paths
          </CardTitle>
          <CardDescription>Most popular professional development paths</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
            <Card className="overflow-hidden">
              <div className="h-2 bg-blue-500 w-full"></div>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <h4 className="font-semibold">Communication Mastery</h4>
                  <Badge className="bg-blue-100 text-blue-700">1,245 enrolled</Badge>
                </div>
                <div className="mt-4 space-y-2">
                  <div className="flex justify-between text-sm">
                    <span>Active Listening</span>
                    <span className="text-green-600">98% completion</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span>Nonverbal Communication</span>
                    <span className="text-green-600">92% completion</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span>Conflict Mediation</span>
                    <span className="text-amber-600">76% completion</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="overflow-hidden">
              <div className="h-2 bg-purple-500 w-full"></div>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <h4 className="font-semibold">Crisis Management</h4>
                  <Badge className="bg-purple-100 text-purple-700">1,018 enrolled</Badge>
                </div>
                <div className="mt-4 space-y-2">
                  <div className="flex justify-between text-sm">
                    <span>Emergency Response</span>
                    <span className="text-green-600">95% completion</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span>De-escalation Techniques</span>
                    <span className="text-green-600">89% completion</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span>Family Communication</span>
                    <span className="text-amber-600">72% completion</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="overflow-hidden">
              <div className="h-2 bg-green-500 w-full"></div>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <h4 className="font-semibold">Emotional Intelligence</h4>
                  <Badge className="bg-green-100 text-green-700">982 enrolled</Badge>
                </div>
                <div className="mt-4 space-y-2">
                  <div className="flex justify-between text-sm">
                    <span>Empathy Development</span>
                    <span className="text-green-600">94% completion</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span>Self-Awareness</span>
                    <span className="text-green-600">91% completion</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span>Interpersonal Skills</span>
                    <span className="text-amber-600">85% completion</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
